package screens.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import design.AccessibilityGuidelines
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet

/**
 * Profile Completion Screen
 *
 * This screen is shown after first-time OIDC login when the user needs to complete their profile.
 * Required fields: username, displayName
 */
@Composable
fun ProfileCompletionScreen(
    onProfileComplete: () -> Unit,
    authViewModel: AuthViewModel,
) {
    val state by authViewModel.state.collectAsState()

    // Load profile on mount
    LaunchedEffect(Unit) {
        authViewModel.onEvent(AuthEvent.OnLoadProfile)
    }

    // Navigate on successful profile save
    LaunchedEffect(state.profileSaveSuccess) {
        if (state.profileSaveSuccess) {
            // Wait a moment to show success message
            onProfileComplete()
        }
    }

    // Announce errors to screen readers
    LaunchedEffect(state.errorMessage) {
        if (state.errorMessage != null) {
            AccessibilityGuidelines.announceError(state.errorMessage!!)
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text("プロフィールを完了してください")
            }

            P({
                style {
                    property("color", "#666")
                    property("margin-bottom", "30px")
                    property("font-size", "14px")
                    property("text-align", "center")
                }
            }) {
                Text("初回ログインありがとうございます。アカウントを設定するために、いくつかの情報を入力してください。")
            }

            // Username input
            Div({
                style {
                    property("margin-bottom", "20px")
                }
            }) {
                Label("username-input", {
                    style {
                        property("display", "block")
                        property("margin-bottom", "8px")
                        property("font-weight", "600")
                        property("color", "#333")
                    }
                }) {
                    Text("ユーザー名 *")
                    Span({
                        style {
                            property("color", "#dc3545")
                            property("margin-left", "4px")
                        }
                    }) {
                        Text("(必須)")
                    }
                }

                Input(
                    type = InputType.Text,
                    attrs = {
                        id("username-input")
                        classes(AppStyleSheet.textInput)
                        attr("name", "username")
                        attr("autocomplete", "username")
                        attr("required", "true")
                        attr("minlength", "3")
                        attr("maxlength", "20")
                        attr("pattern", "^[a-zA-Z0-9_-]+$")
                        attr("aria-invalid", if (state.errorMessage != null) "true" else "false")
                        attr("aria-describedby", "username-hint")
                        value(state.profileUsernameInput)
                        placeholder("ユーザー名を入力")
                        onInput { event ->
                            authViewModel.onEvent(
                                AuthEvent.OnProfileUsernameChanged(event.value),
                            )
                        }
                    },
                )

                Div({
                    id("username-hint")
                    style {
                        property("font-size", "12px")
                        property("color", "#666")
                        property("margin-top", "6px")
                    }
                }) {
                    Text("3〜20文字の英数字、ハイフン、アンダースコア")
                }
            }

            // Display name input
            Div({
                style {
                    property("margin-bottom", "20px")
                }
            }) {
                Label("displayname-input", {
                    style {
                        property("display", "block")
                        property("margin-bottom", "8px")
                        property("font-weight", "600")
                        property("color", "#333")
                    }
                }) {
                    Text("表示名 *")
                    Span({
                        style {
                            property("color", "#dc3545")
                            property("margin-left", "4px")
                        }
                    }) {
                        Text("(必須)")
                    }
                }

                Input(
                    type = InputType.Text,
                    attrs = {
                        id("displayname-input")
                        classes(AppStyleSheet.textInput)
                        attr("name", "displayName")
                        attr("autocomplete", "nickname")
                        attr("required", "true")
                        attr("maxlength", "100")
                        attr("aria-invalid", if (state.errorMessage != null) "true" else "false")
                        value(state.profileDisplayNameInput)
                        placeholder("表示名を入力")
                        onInput { event ->
                            authViewModel.onEvent(
                                AuthEvent.OnProfileDisplayNameChanged(event.value),
                            )
                        }
                    },
                )

                Div({
                    style {
                        property("font-size", "12px")
                        property("color", "#666")
                        property("margin-top", "6px")
                    }
                }) {
                    Text("100文字以下")
                }
            }

            // Error message
            if (state.errorMessage != null) {
                Div({
                    classes(AppStyleSheet.formError)
                    style {
                        property("background-color", "#f8d7da")
                        property("border", "1px solid #f5c6cb")
                        property("color", "#721c24")
                        property("padding", "12px")
                        property("border-radius", "4px")
                        property("margin-bottom", "20px")
                        property("font-size", "14px")
                    }
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(state.errorMessage!!)
                }
            }

            // Save button
            Button(
                attrs = {
                    classes(AppStyleSheet.submitButton)
                    attr("type", "submit")
                    attr("aria-busy", if (state.profileSaving) "true" else "false")
                    attr("aria-label", if (state.profileSaving) "保存中" else "プロフィールを保存")
                    if (state.profileSaving || state.profileUsernameInput.isEmpty() || state.profileDisplayNameInput.isEmpty()) {
                        disabled()
                    }
                    onClick { _ ->
                        authViewModel.onEvent(AuthEvent.OnSaveProfile)
                    }
                },
            ) {
                if (state.profileSaving) {
                    // Loading spinner
                    Span({
                        style {
                            property("display", "inline-block")
                            property("width", "16px")
                            property("height", "16px")
                            property("border", "2px solid #ffffff")
                            property("border-top", "2px solid transparent")
                            property("border-radius", "50%")
                            property("animation", "spin 0.8s linear infinite")
                            property("margin-right", "8px")
                        }
                        attr("role", "status")
                        attr("aria-hidden", "true")
                    }) {}
                    Text("保存中...")
                } else {
                    Text("プロフィールを保存")
                }
            }
        }
    }

    // Add keyframes for spinner animation
    Style {
        """
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        """
    }
}
